import { useState } from 'react';
import Layout from '../components/layout/Layout';
import SectionLabel from '../components/shared/SectionLabel';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import { useBrands } from '../hooks/useApi';
import SEO from '../components/shared/SEO';
import JsonLd from '../components/shared/JsonLd';
import Breadcrumb from '../components/shared/Breadcrumb';
import { getPortfolioBrandUrl } from '../constants/portfolioLinks';
import '../styles/portfoliopage.css';

export default function PortfolioPage() {
  const { data: brands, isLoading } = useBrands();
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const categories = brands
    ? Array.from(new Set(brands.map((brand) => brand.category)))
    : [];

  const filtered = activeCategory
    ? (brands || []).filter((brand) => brand.category === activeCategory)
    : brands || [];

  return (
    <Layout>
      <div className="portfolio-page">
        <SEO
          title="Portfolio - Apex DeFi Labs Inc. Brand Ventures"
          description="Explore Apex DeFi Labs Inc. portfolio of 4 brand ventures spanning DeFi, AI, healthcare technology, and wealth management."
          url="https://apexholdings.com/portfolio"
          type="website"
          keywords="Apex DeFi Labs Inc. portfolio, Veep Capital, Nova Era Labs, OmniHealthX, VruddhiX"
        />
        <JsonLd
          data={{
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Apex DeFi Labs Inc. Portfolio',
            url: 'https://apexholdings.com/portfolio',
            description: 'Portfolio of Apex DeFi Labs Inc. brand ventures.',
            hasPart: [
              { '@type': 'Organization', name: 'Veep Capital' },
              { '@type': 'Organization', name: 'Nova Era Labs' },
              { '@type': 'Organization', name: 'OmniHealthX' },
              { '@type': 'Organization', name: 'VruddhiX' },
            ],
          }}
        />

        <section className="portfolio-page-hero">
          <div className="portfolio-page-hero-overlay" />

          <div className="portfolio-page-shell">
            <div className="portfolio-page-breadcrumb">
              <Breadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Portfolio' }]} />
            </div>
            <SectionLabel>Brand Ventures</SectionLabel>
            <h1 className="portfolio-page-title">
              Our Portfolio of <em>Innovations</em>
            </h1>
            <p className="portfolio-page-subtitle">
              Four brand ventures spanning DeFi, AI, healthcare technology, and wealth management.
            </p>
          </div>
        </section>

        <section className="portfolio-page-catalog">
          <div className="portfolio-page-shell">
            <div className="portfolio-page-filter-list">
              <button
                type="button"
                onClick={() => setActiveCategory(null)}
                className={`portfolio-page-filter${!activeCategory ? ' is-active' : ''}`}
              >
                All
              </button>
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  className={`portfolio-page-filter${activeCategory === category ? ' is-active' : ''}`}
                >
                  {category}
                </button>
              ))}
            </div>

            <div className="portfolio-page-grid">
              {isLoading
                ? Array.from({ length: 6 }).map((_, index) => (
                    <LoadingSkeleton key={index} className="portfolio-page-skeleton" />
                  ))
                : filtered.map((brand) => {
                    const brandUrl = getPortfolioBrandUrl(brand);

                    return (
                      <article key={brand._id} className="portfolio-page-card">
                        <span className="portfolio-page-tag">{brand.tag}</span>
                        <h2 className="portfolio-page-card-title">{brand.name}</h2>
                        {brand.description?.trimStart().startsWith('<')
                          ? <div className="portfolio-page-card-description" dangerouslySetInnerHTML={{ __html: brand.description }} />
                          : <p className="portfolio-page-card-description">{brand.description}</p>
                        }

                        <div className="portfolio-page-card-footer">
                          <span className="portfolio-page-card-category">{brand.category}</span>
                          {brandUrl ? (
                            <a
                              href={brandUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="portfolio-page-card-link"
                            >
                              Visit &rarr;
                            </a>
                          ) : null}
                        </div>
                      </article>
                    );
                  })}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
